import { useAtom } from 'jotai/index';
import { useNavigate } from 'react-router-dom';
import {
  SOUNDPROOFING_SIDEBAR_MODES,
  soundproofingIsResultsVisibleAtom,
  soundproofingLayersAtom,
  soundproofingPdfLoadingAtom,
  soundproofingProductTypesAtom,
  soundproofingRangesAtom,
  soundproofingResultsAtom,
  soundproofingResultsErrorAtom,
  soundproofingResultsFiltersAtom,
  soundproofingResultsLoadingAtom,
  soundproofingResultsPageAtom,
  soundproofingResultsPageSizeAtom,
  soundproofingResultsTotalAtom,
  soundproofingSearchQueryAtom,
  soundproofingSelectedLayerIdAtom,
  soundproofingSelectedResultsIdsAtom,
  soundproofingSidebarModeAtom,
} from '../atoms/soundproofingAtoms';
import { configuratorCalculationsAtom, configuratorSingleConfigurationAtom } from '../atoms/configuratorAtoms';
import { closeModalAtom, openConfirmModalAtom } from '../atoms/modalAtoms';
import SoundproofingServices from '../services/SoundproofingServices';
import { SOUNDPROOFING_MAX_PDF_CONFIGURATIONS } from '../constants/soundproofing';
import useTranslations from './useTranslations';
import { DICTIONARY } from '../translations/dictionary';
import sleep from '../helpers/sleep';

// TODO: remove when the API is ready
const MOCK_REQUEST_DELAY = 600;

// Shared between all the hook instances, so a response of an outdated request is ignored
let lastResultsRequestId = 0;

const useSoundproofing = () => {
  const { t } = useTranslations();
  const navigate = useNavigate();

  const [layers, setLayers] = useAtom(soundproofingLayersAtom);
  const [selectedLayerId, setSelectedLayerId] = useAtom(soundproofingSelectedLayerIdAtom);
  const [productTypes, setProductTypes] = useAtom(soundproofingProductTypesAtom);
  const [ranges, setRanges] = useAtom(soundproofingRangesAtom);
  const [sidebarMode, setSidebarMode] = useAtom(soundproofingSidebarModeAtom);

  const [isResultsVisible, setIsResultsVisible] = useAtom(soundproofingIsResultsVisibleAtom);
  const [searchQuery, setSearchQuery] = useAtom(soundproofingSearchQueryAtom);
  const [results, setResults] = useAtom(soundproofingResultsAtom);
  const [resultsTotal, setResultsTotal] = useAtom(soundproofingResultsTotalAtom);
  const [resultsPage, setResultsPage] = useAtom(soundproofingResultsPageAtom);
  const [resultsPageSize, setResultsPageSize] = useAtom(soundproofingResultsPageSizeAtom);
  const [resultsLoading, setResultsLoading] = useAtom(soundproofingResultsLoadingAtom);
  const [resultsError, setResultsError] = useAtom(soundproofingResultsErrorAtom);
  const [resultsFilters, setResultsFilters] = useAtom(soundproofingResultsFiltersAtom);
  const [selectedResultsIds, setSelectedResultsIds] = useAtom(soundproofingSelectedResultsIdsAtom);
  const [pdfLoading, setPdfLoading] = useAtom(soundproofingPdfLoadingAtom);

  const [, setConfiguratorSingleConfiguration] = useAtom(configuratorSingleConfigurationAtom);
  const [, setConfiguratorCalculations] = useAtom(configuratorCalculationsAtom);
  const [, openConfirmModal] = useAtom(openConfirmModalAtom);
  const [, closeModal] = useAtom(closeModalAtom);

  const selectedLayer = layers.find((layer) => layer.id === selectedLayerId) || null;

  /* Composition Start */

  const selectLayer = (id) => {
    setSelectedLayerId(id);
    setSidebarMode(SOUNDPROOFING_SIDEBAR_MODES.composition);
  };

  const deselectLayer = () => {
    setSelectedLayerId(null);
  };

  const addLayers = () => {
    setLayers((prev) => SoundproofingServices.addLayers(prev));
  };

  const removeLayer = (index) => {
    const removedIds = [layers[index]?.id, layers[index - 1]?.id];
    if (removedIds.includes(selectedLayerId)) setSelectedLayerId(null);
    setLayers((prev) => SoundproofingServices.removeLayer(prev, index));
  };

  const updateLayerParams = (id, getNewParams) => {
    setLayers((prev) => prev.map((layer) => (
      layer.id === id ? { ...layer, params: getNewParams(layer.params) } : layer
    )));
  };

  const clearLayerParams = (id) => {
    setLayers((prev) => prev.map((layer) => (
      layer.id === id ? { ...layer, params: SoundproofingServices.getDefaultLayerParams(layer.type) } : layer
    )));
  };

  const toggleProductType = (key) => {
    setProductTypes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const changeRange = (key, value) => {
    setRanges((prev) => ({ ...prev, [key]: value }));
  };

  const resetResults = () => {
    setIsResultsVisible(false);
    setSearchQuery(null);
    setResults([]);
    setResultsTotal(0);
    setResultsPage(1);
    setResultsError(false);
    setResultsFilters(SoundproofingServices.getDefaultResultsFilters());
    setSelectedResultsIds([]);
  };

  const resetConfiguration = () => {
    setLayers(SoundproofingServices.getInitialLayers());
    setSelectedLayerId(null);
    setProductTypes(SoundproofingServices.getDefaultProductTypes());
    setRanges(SoundproofingServices.getDefaultRanges());
    setSidebarMode(SOUNDPROOFING_SIDEBAR_MODES.composition);
    resetResults();
  };

  const onResetConfigurationClick = () => {
    openConfirmModal({
      text: t(DICTIONARY.sureToCleanConfiguration),
      onButtonClick: () => {
        resetConfiguration();
        closeModal();
      },
    });
  };

  /* Composition End */

  /* Results Start */

  const getResults = async ({
    query = searchQuery, filters = resultsFilters, page = resultsPage, pageSize = resultsPageSize,
  } = {}) => {
    if (!query) return;

    lastResultsRequestId += 1;
    const requestId = lastResultsRequestId;
    const isOutdated = () => requestId !== lastResultsRequestId;

    setResultsLoading(true);
    setResultsError(false);
    try {
      // TODO: replace the mock with the API request when the endpoint is ready:
      //  const data = await sendRequest({
      //    request: Api.getSoundproofingConfigurations,
      //    payload: { ...query, filters, page, items_on_page: pageSize },
      //    showErrorInModal: false,
      //  });
      await sleep(MOCK_REQUEST_DELAY);
      const { items, total } = SoundproofingServices.getMockResults({
        query, filters, page, pageSize,
      });
      if (isOutdated()) return;

      setResults(items);
      setResultsTotal(total);
    } catch (e) {
      if (isOutdated()) return;
      setResults([]);
      setResultsTotal(0);
      setResultsError(true);
    } finally {
      if (!isOutdated()) setResultsLoading(false);
    }
  };

  const showResults = async () => {
    const query = { layers, productTypes, ranges };
    const filters = SoundproofingServices.getDefaultResultsFilters();

    setSearchQuery(query);
    setResultsFilters(filters);
    setResultsPage(1);
    setSelectedResultsIds([]);
    setSelectedLayerId(null);
    setSidebarMode(SOUNDPROOFING_SIDEBAR_MODES.results);
    setIsResultsVisible(true);

    await getResults({ query, filters, page: 1 });
  };

  const backToComposition = () => {
    setSidebarMode(SOUNDPROOFING_SIDEBAR_MODES.composition);
  };

  const changeResultsPage = (page) => {
    setResultsPage(page);
    getResults({ page }).then();
  };

  const changeResultsPageSize = (pageSize) => {
    setResultsPageSize(pageSize);
    setResultsPage(1);
    getResults({ page: 1, pageSize }).then();
  };

  const applyResultsFilters = (filters) => {
    setResultsFilters(filters);
    setResultsPage(1);
    setSelectedResultsIds([]);
    getResults({ filters, page: 1 }).then();
  };

  const resetResultsFilters = () => {
    applyResultsFilters(SoundproofingServices.getDefaultResultsFilters());
  };

  const isResultSelectionLimitReached = selectedResultsIds.length >= SOUNDPROOFING_MAX_PDF_CONFIGURATIONS;

  const toggleResultSelection = (id) => {
    setSelectedResultsIds((prev) => {
      if (prev.includes(id)) return prev.filter((i) => i !== id);
      if (prev.length >= SOUNDPROOFING_MAX_PDF_CONFIGURATIONS) return prev;
      return [...prev, id];
    });
  };

  const toggleAllResultsOnPage = () => {
    const pageIds = results.map((item) => item.id);
    const isAllSelected = pageIds.length && pageIds.every((id) => selectedResultsIds.includes(id));

    if (isAllSelected) {
      setSelectedResultsIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedResultsIds((prev) => {
        const toAdd = pageIds.filter((id) => !prev.includes(id));
        return [...prev, ...toAdd].slice(0, SOUNDPROOFING_MAX_PDF_CONFIGURATIONS);
      });
    }
  };

  const downloadPdf = async () => {
    if (pdfLoading) return;

    setPdfLoading(true);
    try {
      // TODO: request the pdf of `selectedResultsIds` when the endpoint is ready and save it via FilesServices.downloadPdf.
      //  It is unknown yet what should be downloaded when nothing is selected.
      await sleep(MOCK_REQUEST_DELAY);
    } finally {
      setPdfLoading(false);
    }
  };

  // TODO: accept the clicked result and pass it to the configurator when it is known how the configuration has to be transferred
  const openInConfigurator = () => {
    setConfiguratorSingleConfiguration(null);
    setConfiguratorCalculations(null);
    navigate('/configurator');
  };

  /* Results End */

  return {
    layers,
    selectedLayer,
    productTypes,
    ranges,
    sidebarMode,
    isCompositionSet: SoundproofingServices.isCompositionSet({ layers, productTypes, ranges }),

    isResultsVisible,
    results,
    resultsTotal,
    resultsPage,
    resultsPageSize,
    resultsLoading,
    resultsError,
    resultsFilters,
    selectedResultsIds,
    isResultSelectionLimitReached,
    pdfLoading,

    selectLayer,
    deselectLayer,
    addLayers,
    removeLayer,
    updateLayerParams,
    clearLayerParams,
    toggleProductType,
    changeRange,
    onResetConfigurationClick,

    showResults,
    backToComposition,
    changeResultsPage,
    changeResultsPageSize,
    applyResultsFilters,
    resetResultsFilters,
    toggleResultSelection,
    toggleAllResultsOnPage,
    downloadPdf,
    openInConfigurator,
  };
};

export default useSoundproofing;
